import { ArrowLeft, Camera, Loader2, Settings2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { LoginScreen } from "../components/LoginScreen";
import { CategoryManageSheet } from "../components/moments/CategoryManageSheet";
import { MomentReview } from "../components/moments/MomentReview";
import { MomentsCameraView } from "../components/moments/MomentsCameraView";
import { MomentsStatsView } from "../components/moments/MomentsStatsView";
import { useToast } from "../components/ToastProvider";
import {
  captureMoment,
  createMomentCategory,
  deleteMoment,
  deleteMomentCategory,
  loadMomentsBootstrap,
  loadSession,
  loginWithCredentials,
  loginWithPasscode,
  registerAccount,
  reorderMomentCategories,
  updateMomentCategory
} from "../lib/api";
import { todayLocalDay } from "../lib/moments";
import { useIsCompact } from "../lib/useMediaQuery";
import type { Moment, MomentCategoryKind, MomentCategoryNode, MomentStats } from "../types";

const EMPTY_STATS: MomentStats = {
  timeline: [],
  perCategory: [],
  routineCompletion: { done: 0, total: 0 },
  habitStreaks: []
};

type View = "camera" | "review" | "stats";

// State hub for the Moments side-branch (mirrors the "state lives in the page" rule). Immersive
// route — does NOT use AppShell so the camera can go full-screen.
export function Moments() {
  const { notify } = useToast();
  const isCompact = useIsCompact();

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [view, setView] = useState<View>("stats");
  const [categories, setCategories] = useState<MomentCategoryNode[]>([]);
  const [moments, setMoments] = useState<Moment[]>([]);
  const [stats, setStats] = useState<MomentStats>(EMPTY_STATS);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);

  const refresh = useCallback(async () => {
    const payload = await loadMomentsBootstrap(todayLocalDay());
    setCategories(payload.categories);
    setMoments(payload.moments);
    setStats(payload.stats);
  }, []);

  useEffect(() => {
    let mounted = true;
    loadSession()
      .then(async (session) => {
        if (!mounted) {
          return;
        }
        setIsAuthenticated(session.authenticated);
        if (session.authenticated) {
          await refresh();
          if (mounted) {
            setView(window.matchMedia("(max-width: 1023px)").matches ? "camera" : "stats");
          }
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (mounted) {
          setCheckingAuth(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const completeAuth = async () => {
    setIsAuthenticated(true);
    await refresh();
    setView(window.matchMedia("(max-width: 1023px)").matches ? "camera" : "stats");
  };

  const handleLoginPasscode = async (passcode: string) => {
    setLoginError("");
    try {
      await loginWithPasscode(passcode);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng nhập thất bại");
      throw error;
    }
  };
  const handleLoginCredentials = async (input: { email: string; password: string }) => {
    setLoginError("");
    try {
      await loginWithCredentials(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng nhập thất bại");
      throw error;
    }
  };
  const handleRegister = async (input: { email: string; username: string; password: string }) => {
    setLoginError("");
    try {
      await registerAccount(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng ký thất bại");
      throw error;
    }
  };

  const handleCaptured = (file: File) => {
    setCapturedFile(file);
    setView("review");
  };

  const handleRetake = () => {
    setCapturedFile(null);
    setView("camera");
  };

  const handleSubmit = async ({ categoryId, note }: { categoryId: string | null; note: string }) => {
    if (!capturedFile) {
      return;
    }
    setSaving(true);
    try {
      await captureMoment({
        file: capturedFile,
        categoryId,
        note: note || undefined,
        loggedAt: new Date().toISOString(),
        localDay: todayLocalDay()
      });
      notify({ type: "success", title: "Đã lưu khoảnh khắc" });
      setCapturedFile(null);
      await refresh();
      setView(isCompact ? "camera" : "stats");
    } catch (error) {
      notify({
        type: "error",
        title: "Lưu thất bại",
        description: error instanceof Error ? error.message : undefined
      });
      // keep capturedFile so the user can retry from the review screen
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMoment = async (id: string) => {
    try {
      await deleteMoment(id);
      await refresh();
      notify({ type: "success", title: "Đã xoá" });
    } catch (error) {
      notify({ type: "error", title: "Xoá thất bại", description: error instanceof Error ? error.message : undefined });
    }
  };

  const runCategoryOp = async (op: () => Promise<unknown>, errorTitle: string) => {
    setManageBusy(true);
    try {
      await op();
      await refresh();
    } catch (error) {
      notify({ type: "error", title: errorTitle, description: error instanceof Error ? error.message : undefined });
    } finally {
      setManageBusy(false);
    }
  };

  const handleCreateCategory = (input: { parentId: string; kind: MomentCategoryKind; name: string }) =>
    runCategoryOp(() => createMomentCategory(input), "Không tạo được nhóm");
  const handleRenameCategory = (id: string, name: string) =>
    runCategoryOp(() => updateMomentCategory(id, { name }), "Không đổi tên được");
  const handleDeleteCategory = (id: string) => runCategoryOp(() => deleteMomentCategory(id), "Không xoá được nhóm");
  const handleReorderCategory = (items: Array<{ id: string; sortOrder: number; parentId: string | null }>) =>
    runCategoryOp(() => reorderMomentCategories(items), "Không sắp xếp được");

  if (checkingAuth) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-paper text-muted">
        <Loader2 size={22} className="animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        onLoginCredentials={handleLoginCredentials}
        onRegister={handleRegister}
        onLoginPasscode={handleLoginPasscode}
        error={loginError}
      />
    );
  }

  if (view === "camera") {
    return <MomentsCameraView onCaptured={handleCaptured} onClose={() => setView("stats")} />;
  }

  if (view === "review" && capturedFile) {
    return (
      <MomentReview
        file={capturedFile}
        categories={categories}
        saving={saving}
        onRetake={handleRetake}
        onSubmit={handleSubmit}
        onManage={() => setManageOpen(true)}
      />
    );
  }

  return (
    <div className="min-h-[100dvh] bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            <ArrowLeft size={17} aria-hidden="true" />
            Human Note
          </a>
          <p className="text-sm font-bold text-ink">Khoảnh khắc</p>
          {isCompact ? (
            <button
              type="button"
              onClick={() => setView("camera")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-bold text-white hover:bg-blue-700"
            >
              <Camera size={16} aria-hidden="true" />
              Chụp
            </button>
          ) : (
            <span className="text-xs font-medium text-muted">Chụp ảnh trên điện thoại</span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-4">
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setManageOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-semibold text-ink hover:bg-slate-50"
          >
            <Settings2 size={15} aria-hidden="true" />
            Quản lý nhóm
          </button>
        </div>
        <MomentsStatsView
          moments={moments}
          categories={categories}
          stats={stats}
          filterCategoryId={filterCategoryId}
          onFilter={setFilterCategoryId}
          onDelete={handleDeleteMoment}
        />
      </main>

      {manageOpen ? (
        <CategoryManageSheet
          categories={categories}
          busy={manageBusy}
          onClose={() => setManageOpen(false)}
          onCreate={handleCreateCategory}
          onRename={handleRenameCategory}
          onDelete={handleDeleteCategory}
          onReorder={handleReorderCategory}
        />
      ) : null}
    </div>
  );
}
